import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    const data = event.detail.data;
    if (data.newProduct?.id) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.morphedMain === true && data.productId) {
      this.dataset.productId = String(data.productId);
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const installmentInput = this.querySelector('form.payment-terms input[name="id"]');
    if (installmentInput instanceof HTMLInputElement) {
      const id = event.detail.resource?.id;
      installmentInput.value = id != null ? String(id) : '';
      installmentInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (data.morphedMain === true) return;

    const { priceContainer, volumePricingNote } = this.refs;
    const fetched = data.html;
    const priceSectionDomId = this.closest('.shopify-section')?.id;
    /** @type {ParentNode} */
    let priceSearchRoot = fetched;
    if (priceSectionDomId && fetched instanceof Document) {
      const sectionEl = fetched.getElementById(priceSectionDomId);
      if (sectionEl) priceSearchRoot = sectionEl;
    }
    const newProductPrice = priceSearchRoot.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!(newProductPrice instanceof HTMLElement)) return;

    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) {
      currentPrice.replaceWith(newPrice);
    }

    const priceContainerEl = this.querySelector('[ref="priceContainer"]');
    const currentPromo = this.querySelector('.price-promo-messaging');
    const newPromo = newProductPrice.querySelector('.price-promo-messaging');
    if (!newPromo) {
      currentPromo?.remove();
    } else if (!currentPromo) {
      priceContainerEl?.insertAdjacentElement('afterend', /** @type {Element} */ (newPromo.cloneNode(true)));
    } else {
      currentPromo.replaceWith(newPromo);
    }

    const currentNote = this.querySelector('[ref="volumePricingNote"]');
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
    const noteInsertAnchor = this.querySelector('.price-promo-messaging') ?? priceContainerEl;
    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      noteInsertAnchor?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
